import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AlertTriangle, ArrowLeft, Search } from "lucide-react";

/**
 * Direct-link resolver: /cs/:displayId
 *
 * Looks up the Care Seeker by their human-friendly CSK#### code
 * and redirects to the canonical /care-seekers/:uuid profile.
 *
 * Lets staff paste `/cs/CSK5501` into Slack / email / chat and have
 * teammates land on the right profile in one click.
 */
export default function CareSeekerDirectLink() {
  const { displayId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const { data } = await api.get(`/care-seekers/by-display-id/${encodeURIComponent(displayId)}`);
        if (cancelled) return;
        navigate(`/care-seekers/${data.id}`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(formatApiError(e));
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [displayId, navigate]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-10" data-testid="cs-direct-link-error">
        <Link to="/care-seekers" className="inline-flex items-center gap-2 text-sm text-[#1E3A8A] hover:text-[#1E3A8A] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Care Seekers
        </Link>
        <div className="bg-white border border-[#FCA5A5] rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-[#FEE2E2] text-[#991B1B] flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-xl font-semibold text-[#0F172A] mb-2">
            We couldn't find <span className="font-mono">{displayId}</span>
          </h2>
          <p className="text-sm text-[#334155] mb-6">{error}</p>
          <Link
            to="/care-seekers"
            data-testid="cs-direct-link-fallback"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
          >
            <Search className="w-4 h-4" /> Browse all Care Seekers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-16 text-[#1E3A8A]" data-testid="cs-direct-link-loading">
      Opening <span className="font-mono">{displayId}</span>…
    </div>
  );
}
